import { useEffect, useMemo, useState } from "react";
import {
  useGetCustomerQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
} from "../../../../redux/api";
import type { Customer as CustomerModel } from "../../../../redux/api";
import { validateEmail, validatePhone } from "../../../../utils/validation";
import { PhoneInput } from "../../../../components/PhoneInput";
import { Modal } from "../../../../components/Modal";
import { EntityMeta } from "../../../../components/EntityMeta";
import { Field } from "../../../../components/Field";
import { statusTone } from "../../../../utils/status";

interface CustomerFormModalProps {
  open: boolean;
  /** null = create */
  id: string | null;
  onClose: () => void;
}

type CustomerForm = {
  name: string;
  code: string;
  email: string;
  phone: string;
  gstin: string;
  contactPerson: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  creditLimit: string;
  status: string;
};

const emptyForm: CustomerForm = {
  name: "",
  code: "",
  email: "",
  phone: "",
  gstin: "",
  contactPerson: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  country: "India",
  postalCode: "",
  creditLimit: "",
  status: "active",
};

const formFromCustomer = (c: CustomerModel): CustomerForm => ({
  name: c.name ?? "",
  code: c.code ?? "",
  email: c.email ?? "",
  phone: c.phone ?? "",
  gstin: c.gstin ?? "",
  contactPerson: c.contactPerson ?? "",
  addressLine1: c.addressLine1 ?? "",
  addressLine2: c.addressLine2 ?? "",
  city: c.city ?? "",
  state: c.state ?? "",
  country: c.country ?? "India",
  postalCode: c.postalCode ?? "",
  creditLimit: c.creditLimit != null ? String(c.creditLimit) : "",
  status: c.status ?? "active",
});

const num = (value: string): number | undefined =>
  value.trim() === "" ? undefined : Number(value);

// Inventory → Customer add/edit popup.
export const CustomerFormModal = ({ open, id, onClose }: CustomerFormModalProps) => {
  const [activeId, setActiveId] = useState<string | null>(id);
  const isEdit = !!activeId;

  const { data: existing } = useGetCustomerQuery(activeId ?? "", {
    skip: !activeId,
  });

  const [createCustomer, { isLoading: creating }] = useCreateCustomerMutation();
  const [updateCustomer, { isLoading: updating }] = useUpdateCustomerMutation();

  const [form, setForm] = useState<CustomerForm>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});

  const saving = creating || updating;

  // Re-sync to the record being opened every time the popup opens (or the
  // target id changes while open) — this component stays mounted between
  // opens, so state from a previous visit would otherwise linger.
  useEffect(() => {
    if (!open) return;
    setActiveId(id);
    setFormError(null);
    setFieldErrors({});
    if (id && existing && existing.id === id) {
      setForm(formFromCustomer(existing));
    } else if (!id) {
      setForm(emptyForm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, id]);

  useEffect(() => {
    if (existing) setForm(formFromCustomer(existing));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing?.id, existing?.updatedAt]);

  const setField = (key: keyof CustomerForm, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const baseline = useMemo(
    () => (existing ? formFromCustomer(existing) : emptyForm),
    [existing],
  );
  const dirty = JSON.stringify(form) !== JSON.stringify(baseline);

  const handleSave = async () => {
    setFormError(null);

    const errors: Record<string, string> = {};
    if (!form.name.trim()) errors.name = "Name is required.";
    const emailError = validateEmail(form.email, false);
    if (emailError) errors.email = emailError;
    const phoneError = validatePhone(form.phone);
    if (phoneError) errors.phone = phoneError;

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    const payload = {
      name: form.name.trim(),
      code: form.code.trim() || undefined,
      email: form.email.trim() || undefined,
      phone: form.phone.trim() || undefined,
      gstin: form.gstin.trim() || undefined,
      contactPerson: form.contactPerson.trim() || undefined,
      addressLine1: form.addressLine1.trim() || undefined,
      addressLine2: form.addressLine2.trim() || undefined,
      city: form.city.trim() || undefined,
      state: form.state.trim() || undefined,
      country: form.country.trim() || undefined,
      postalCode: form.postalCode.trim() || undefined,
      creditLimit: num(form.creditLimit),
      status: form.status,
    };

    try {
      if (isEdit && activeId) {
        await updateCustomer({ id: activeId, input: payload }).unwrap();
      } else {
        await createCustomer({ ...payload }).unwrap();
      }
      onClose();
    } catch (err) {
      setFormError(
        (err as { message?: string })?.message ?? "Failed to save customer.",
      );
    }
  };

  return (
    <Modal
      open={open}
      title={isEdit ? "Edit customer" : "New customer"}
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleSave}
            disabled={saving || (isEdit && !dirty)}
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create customer"}
          </button>
        </>
      }
    >
      <EntityMeta
        id={activeId ?? undefined}
        status={isEdit ? { label: form.status, tone: statusTone(form.status) } : undefined}
      />

      {formError ? <div className="form-error">{formError}</div> : null}

      <div className="form-grid">
        <Field label="Name *" error={fieldErrors.name}>
          <input
            value={form.name}
            onChange={(e) => {
              setField("name", e.target.value);
              setFieldErrors((prev) => (prev.name ? { ...prev, name: undefined } : prev));
            }}
          />
        </Field>
        <label className="field">
          Code
          <input
            value={form.code}
            onChange={(e) => setField("code", e.target.value)}
            placeholder="CUST-001"
          />
        </label>
        <Field label="Email" error={fieldErrors.email}>
          <input
            type="email"
            value={form.email}
            onChange={(e) => {
              setField("email", e.target.value);
              setFieldErrors((prev) => (prev.email ? { ...prev, email: undefined } : prev));
            }}
          />
        </Field>
        <Field label="Phone" error={fieldErrors.phone}>
          <PhoneInput
            value={form.phone}
            onChange={(v) => {
              setField("phone", v);
              setFieldErrors((prev) => (prev.phone ? { ...prev, phone: undefined } : prev));
            }}
          />
        </Field>
        <label className="field">
          GSTIN
          <input
            value={form.gstin}
            onChange={(e) => setField("gstin", e.target.value)}
          />
        </label>
        <label className="field">
          Contact person
          <input
            value={form.contactPerson}
            onChange={(e) => setField("contactPerson", e.target.value)}
          />
        </label>
        <label className="field field--full">
          Address line 1
          <input
            value={form.addressLine1}
            onChange={(e) => setField("addressLine1", e.target.value)}
          />
        </label>
        <label className="field field--full">
          Address line 2
          <input
            value={form.addressLine2}
            onChange={(e) => setField("addressLine2", e.target.value)}
          />
        </label>
        <label className="field">
          City
          <input
            value={form.city}
            onChange={(e) => setField("city", e.target.value)}
          />
        </label>
        <label className="field">
          State
          <input
            value={form.state}
            onChange={(e) => setField("state", e.target.value)}
          />
        </label>
        <label className="field">
          Country
          <input
            value={form.country}
            onChange={(e) => setField("country", e.target.value)}
          />
        </label>
        <label className="field">
          Postal code
          <input
            value={form.postalCode}
            onChange={(e) => setField("postalCode", e.target.value)}
          />
        </label>
        <label className="field">
          Credit limit
          <input
            type="number"
            value={form.creditLimit}
            onChange={(e) => setField("creditLimit", e.target.value)}
          />
        </label>
        <label className="field">
          Status
          <select
            value={form.status}
            onChange={(e) => setField("status", e.target.value)}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </label>
      </div>
    </Modal>
  );
};
